import { t } from '@lingui/macro'
import { useNavigate } from 'react-router-dom-original'
import styled, { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Markdown } from '@/@bothrs-ui/web/components/markdown/Markdown'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'
import { NativePlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { StErrorLayout } from './ErrorLayout.styled'

import type { FC } from 'react'

type ErrorLayoutProps = {
  showHeader?: boolean
  title: string
  message: string
  markdown?: boolean
  imgSrc?: string
  onTitleClick?: () => void
  cta?: {
    label: string
    onClick: () => void
  }
}

export const ErrorLayout: FC<ErrorLayoutProps> = ({
  showHeader = false,
  title,
  message,
  markdown = false,
  imgSrc = '/img/svg/timeline-error.svg',
  onTitleClick,
  cta,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()

  return (
    <ContentPageLayout>
      {showHeader ? (
        <NativePlatform>
          <AppHeader
            title={t({
              id: 'errors.not-found.title',
              message: 'Er gaat iets mis',
            })}
            primaryAction={{
              label: 'Terug',
              icon: ['far', 'chevron-left'],
              onClick: () => navigate(-1),
            }}
          />
        </NativePlatform>
      ) : null}
      <StContent>
        <StErrorLayout>
          <FlexColumn justifyContent='center' alignItems='center' flexGrow={1} gap={theme.UI.Spacing.Space4}>
            <StIllustration src={imgSrc} alt='error' />
            <StTitle noMargin textAlign='center' onClick={onTitleClick}>
              {title}
            </StTitle>
            {markdown ? (
              <StMarkdownWrapper>
                <Markdown content={message} />
              </StMarkdownWrapper>
            ) : (
              <Span type='BodyMediumRegular' textAlign='center'>
                {message}
              </Span>
            )}
            {cta ? (
              <Button variant='Primary' onClick={cta.onClick}>
                {cta.label}
              </Button>
            ) : null}
          </FlexColumn>
        </StErrorLayout>
      </StContent>
    </ContentPageLayout>
  )
}

const StIllustration = styled.img`
  max-width: 100%;
  height: auto;
`

const StTitle = styled(H2)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
`

const StContent = styled(FlexColumn)`
  max-width: 600px;
  width: 100%;
  flex: 1;
  align-self: center;
`

const StMarkdownWrapper = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};

  strong {
    color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  }
`
